// This is the ONLY file that imports from @mui/x-date-pickers.
// To revert to a different calendar implementation, replace only this file.
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { PickerSelectionState } from "@mui/x-date-pickers/internals";
import { DateTime } from "luxon";

const DateCalendarComponent = ({
  value,
  onChange,
  min,
  max,
}: {
  value: string;
  onChange: (isoDate: string) => void;
  min?: string;
  max?: string;
}) => {
  const luxonValue = value ? DateTime.fromISO(value) : null;
  const minDate = min ? DateTime.fromISO(min) : undefined;
  const maxDate = max ? DateTime.fromISO(max) : undefined;

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale="da">
      <DateCalendar
        value={luxonValue}
        views={["month", "day"]}
        openTo="day"
        onChange={(
          newValue: DateTime | null,
          selectionState?: PickerSelectionState,
        ) => {
          if (newValue?.isValid && selectionState === "finish") {
            onChange(newValue.set({ hour: 12, minute: 0, second: 0 }).toISO()!);
          }
        }}
        minDate={minDate}
        maxDate={maxDate}
        sx={{
          width: 288,
          height: "auto",
          "& *": { fontFamily: '"Outfit", sans-serif' },

          // Day cells
          "& .MuiPickersDay-root": {
            width: 32,
            height: 32,
            margin: "2px",
            fontSize: 14,
            color: "var(--col-text-primary)",
            "&:hover": { backgroundColor: "var(--col-surface-tertiary)" },
            "&:focus": { backgroundColor: "transparent" },
            "&:focus-visible": {
              outline: "2px solid var(--col-border-focus)",
              outlineOffset: "1px",
            },
          },

          // Selected day
          "& .MuiPickersDay-root.Mui-selected": {
            backgroundColor: "var(--col-interaction-tertiary)",
            color: "#fff",
            "&:hover": { backgroundColor: "var(--col-interaction-tertiary)" },
            "&:focus": { backgroundColor: "var(--col-interaction-tertiary)" },
            "&:focus-visible": {
              outline: "2px solid var(--col-border-focus)",
              outlineOffset: "1px",
            },
          },

          // Today (unselected)
          "& .MuiPickersDay-today:not(.Mui-selected)": {
            border: "1px solid var(--col-interaction-tertiary)",
            color: "var(--col-interaction-tertiary)",
          },

          // Weekday header labels (M T O T F L S)
          "& .MuiDayCalendar-weekDayLabel": {
            width: 32,
            height: 32,
            margin: "2px",
            fontSize: 12,
            color: "var(--col-text-secondary)",
          },

          // Month/year label in header
          "& .MuiPickersCalendarHeader-label": {
            fontSize: 14,
            fontWeight: 500,
            color: "var(--col-text-primary)",
          },

          // Switch-view button — hidden, year view removed
          "& .MuiPickersCalendarHeader-switchViewButton": {
            display: "none",
          },

          // Prev/next month arrows
          "& .MuiPickersArrowSwitcher-button": {
            color: "var(--col-text-primary)",
            "&:hover": { backgroundColor: "var(--col-surface-tertiary)" },
            "&:disabled": { color: "var(--col-graph-first-1)" },
          },

          // Month grid container
          "& .MuiMonthCalendar-root": {
            width: "100%",
            padding: "0 8px 8px",
          },

          // Month picker cells
          "& .MuiPickersMonth-monthButton": {
            fontSize: 14,
            borderRadius: "var(--border-radius-full)",
            "&:hover": { backgroundColor: "var(--col-surface-tertiary)" },
            "&.Mui-selected": {
              backgroundColor: "var(--col-interaction-tertiary)",
              color: "#fff",
              "&:hover": {
                backgroundColor: "var(--col-interaction-tertiary)",
              },
            },
            "&.Mui-disabled": {
              color: "var(--col-text-secondary)",
              opacity: 1,
            },
          },

          // Year picker cells
          "& .MuiPickersYear-yearButton": {
            fontSize: 14,
            borderRadius: "var(--border-radius-full)",
            "&:hover": { backgroundColor: "var(--col-surface-tertiary)" },
            "&.Mui-selected": {
              backgroundColor: "var(--col-interaction-tertiary)",
              color: "#fff",
              "&:hover": {
                backgroundColor: "var(--col-interaction-tertiary)",
              },
            },
          },
        }}
      />
    </LocalizationProvider>
  );
};

export default DateCalendarComponent;
